import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";

const PROJECT_TYPES = [
  { value: "coffrage", label: "Coffrage", icon: "fa-solid fa-grip" },
  { value: "planchers", label: "Planchers béton", icon: "fa-solid fa-layer-group" },
  { value: "platelage", label: "Platelage poutres", icon: "fa-solid fa-bars-staggered" },
  { value: "structure", label: "Structure béton", icon: "fa-solid fa-building" },
  { value: "maconnerie", label: "Maçonnerie", icon: "fa-solid fa-trowel-bricks" },
  { value: "nettoyage", label: "Nettoyage chantier / bureaux", icon: "fa-solid fa-broom" },
  { value: "autre", label: "Autre / Mixte", icon: "fa-solid fa-helmet-safety" },
];

export default function Devis() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    project_type: "",
    surface: "",
    location: "",
    start_date: "",
    budget: "",
    description: "",
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [status, setStatus] = useState({ ok: null, msg: "" });
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const svc = params.get("service");
    if (svc && PROJECT_TYPES.some((t) => t.value === svc)) {
      setForm((f) => ({ ...f, project_type: svc }));
    }
  }, [location.search]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ ok: null, msg: "" });
    try {
      await api.post("/quotes", form);
      setStatus({ ok: true, msg: "Demande reçue. Notre bureau d’études vous contacte sous 48h." });
      setStep(4);
    } catch (err) {
      setStatus({ ok: false, msg: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setLoading(false);
    }
  };

  const canNext1 = !!form.project_type;
  const canNext2 = form.description && form.location;

  return (
    <div className="bg-[#0a0a0a] text-white pt-32 pb-24" data-testid="devis-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="eyebrow mb-4">/ Demande de devis · Chantier neuf</div>
        <h1 className="font-display font-bold uppercase tracking-tighter text-5xl md:text-6xl leading-[0.92]">
          Décrivez votre<br /><span className="text-[#ffd600]">chantier en 3 étapes.</span>
        </h1>
        <p className="text-neutral-400 mt-5 max-w-2xl">
          Plus votre brief est précis, plus notre estimation sera fiable. Vous recevrez une réponse de notre BE sous 48 heures ouvrées.
        </p>

        {/* Stepper */}
        <div className="mt-10 grid grid-cols-3 gap-2 max-w-xl" data-testid="devis-stepper">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1.5 ${step >= n ? "bg-[#ffd600]" : "bg-neutral-800"}`} />
          ))}
        </div>
        <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-500 mt-3">
          {step <= 3 ? `Étape ${step} / 3` : "Étape · Confirmation"}
        </div>

        <form onSubmit={submit} className="mt-8">
          {step === 1 && (
            <div className="brut-card !p-8 md:!p-10" data-testid="devis-step-1">
              <h3 className="font-display uppercase text-2xl">Type de prestation principale</h3>
              <p className="text-neutral-400 mt-1 text-sm">Sélectionnez le lot dominant de votre chantier.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                {PROJECT_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setForm((f) => ({ ...f, project_type: t.value }))}
                    data-testid={`devis-type-${t.value}`}
                    className={`p-5 text-left border transition-colors ${
                      form.project_type === t.value
                        ? "border-[#ffd600] bg-[#ffd600]/5"
                        : "border-[#262626] bg-[#141414] hover:border-[#ffd600]/40"
                    }`}
                  >
                    <i className={`${t.icon} text-[#ffd600] text-xl`} />
                    <div className="font-display uppercase mt-3 text-lg">{t.label}</div>
                  </button>
                ))}
              </div>
              <div className="mt-8 flex justify-end">
                <button type="button" disabled={!canNext1} onClick={() => setStep(2)} className="btn-primary" data-testid="devis-next-1">
                  Continuer <i className="fa-solid fa-arrow-right" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="brut-card !p-8 md:!p-10" data-testid="devis-step-2">
              <h3 className="font-display uppercase text-2xl">Détails du chantier</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                <div>
                  <label className="label-field">Lieu du chantier *</label>
                  <input required value={form.location} onChange={set("location")} placeholder="Ville, code postal" className="input-field" data-testid="devis-location" />
                </div>
                <div>
                  <label className="label-field">Surface estimée</label>
                  <input value={form.surface} onChange={set("surface")} placeholder="ex. 1 200 m² SHON" className="input-field" data-testid="devis-surface" />
                </div>
                <div>
                  <label className="label-field">Démarrage souhaité</label>
                  <input value={form.start_date} onChange={set("start_date")} placeholder="ex. T2 2026" className="input-field" data-testid="devis-start" />
                </div>
                <div>
                  <label className="label-field">Budget indicatif</label>
                  <input value={form.budget} onChange={set("budget")} placeholder="ex. 450 000 € HT" className="input-field" data-testid="devis-budget" />
                </div>
              </div>
              <div className="mt-5">
                <label className="label-field">Description du projet *</label>
                <textarea required rows={5} value={form.description} onChange={set("description")} placeholder="Programme, niveaux, contraintes techniques, planning souhaité…" className="input-field resize-none" data-testid="devis-description" />
              </div>
              <div className="mt-8 flex justify-between">
                <button type="button" onClick={() => setStep(1)} className="btn-ghost" data-testid="devis-back-2">
                  <i className="fa-solid fa-arrow-left" /> Retour
                </button>
                <button type="button" disabled={!canNext2} onClick={() => setStep(3)} className="btn-primary" data-testid="devis-next-2">
                  Continuer <i className="fa-solid fa-arrow-right" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="brut-card !p-8 md:!p-10" data-testid="devis-step-3">
              <h3 className="font-display uppercase text-2xl">Vos coordonnées</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                <div>
                  <label className="label-field">Nom complet *</label>
                  <input required value={form.name} onChange={set("name")} className="input-field" data-testid="devis-name" />
                </div>
                <div>
                  <label className="label-field">Société</label>
                  <input value={form.company} onChange={set("company")} className="input-field" data-testid="devis-company" />
                </div>
                <div>
                  <label className="label-field">Email *</label>
                  <input required type="email" value={form.email} onChange={set("email")} className="input-field" data-testid="devis-email" />
                </div>
                <div>
                  <label className="label-field">Téléphone *</label>
                  <input required value={form.phone} onChange={set("phone")} className="input-field" data-testid="devis-phone" />
                </div>
              </div>

              {status.msg && !status.ok && (
                <div className="mt-5 p-4 border border-red-500/40 text-red-300 bg-red-500/10 font-mono-tech text-sm" data-testid="devis-error">
                  {status.msg}
                </div>
              )}

              <div className="mt-8 flex justify-between">
                <button type="button" onClick={() => setStep(2)} className="btn-ghost" data-testid="devis-back-3">
                  <i className="fa-solid fa-arrow-left" /> Retour
                </button>
                <button type="submit" disabled={loading} className="btn-primary" data-testid="devis-submit">
                  {loading ? "Envoi…" : "Envoyer la demande"} <i className="fa-solid fa-paper-plane" />
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="brut-card !p-10 text-center" data-testid="devis-success">
              <div className="w-16 h-16 mx-auto bg-[#ffd600] text-black flex items-center justify-center">
                <i className="fa-solid fa-check text-3xl" />
              </div>
              <h3 className="font-display uppercase text-3xl mt-6">Demande reçue.</h3>
              <p className="text-neutral-300 mt-3 max-w-md mx-auto">{status.msg}</p>
              <a href="/" className="btn-ghost mt-8 inline-flex" data-testid="devis-home">
                Retour à l’accueil
              </a>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
